import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageHeader from "../../components/common/PageHeader";
import Card from "../../components/common/Card";
import Table from "../../components/common/Table";
import Input from "../../components/common/Input";
import Select from "../../components/common/Select";
import Button from "../../components/common/Button";
import Alert from "../../components/common/Alert";
import { useGetPosOrdersQuery } from "../../store/api";

const STATUS_OPTIONS = [
  "IN_KITCHEN",
  "CAPTURED",
  "PAYMENT_FAILED",
  "COMPLETED",
  "CANCELLED",
  "DELIVERED",
];

const CHANNEL_OPTIONS = ["POS", "KIOSK", "QR", "WEB", "SQ"];
const SOURCE_OPTIONS = ["EDGE", "CENTRAL"];

const parseCsv = (value) =>
  value
    ? value
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const toLocalDateTimeInputValue = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const fromLocalDateTimeInputValue = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const getUtcStartOfDay = (offsetDays = 0) => {
  const date = new Date();
  date.setUTCDate(date.getUTCDate() + offsetDays);
  date.setUTCHours(0, 0, 0, 0);
  return date;
};

const formatMoney = (amount) => `$${Number(amount || 0).toFixed(2)}`;

const OrderList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const status = parseCsv(searchParams.get("status"));
  const channels = parseCsv(searchParams.get("channels"));
  const sourceSystems = parseCsv(searchParams.get("sourceSystems"));
  const search = searchParams.get("search") || "";
  const from = searchParams.get("from") || "";
  const to = searchParams.get("to") || "";
  const page = Math.max(Number(searchParams.get("page") || 1), 1);
  const pageSize = [25, 50, 100, 200].includes(
    Number(searchParams.get("pageSize")),
  )
    ? Number(searchParams.get("pageSize"))
    : 50;
  const preset =
    searchParams.get("preset") || (!from && !to ? "today" : "custom");

  const [searchDraft, setSearchDraft] = useState(search);
  const [customFromInput, setCustomFromInput] = useState(
    toLocalDateTimeInputValue(from),
  );
  const [customToInput, setCustomToInput] = useState(
    toLocalDateTimeInputValue(to),
  );
  const [filterError, setFilterError] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(
    status.length > 0 ||
      channels.length > 0 ||
      sourceSystems.length > 0 ||
      preset === "custom",
  );

  useEffect(() => {
    setSearchDraft(search);
  }, [search]);

  useEffect(() => {
    setCustomFromInput(toLocalDateTimeInputValue(from));
    setCustomToInput(toLocalDateTimeInputValue(to));
  }, [from, to]);

  const updateParams = (updates, { resetPage = true } = {}) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length === 0) {
          next.delete(key);
        } else {
          next.set(key, value.join(","));
        }
        return;
      }

      if (value === undefined || value === null || value === "") {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });

    if (resetPage) {
      next.delete("page");
    }

    if (next.toString()) {
      setSearchParams(next);
    } else {
      setSearchParams({});
    }
  };

  const toggleMultiFilter = (key, currentValues, value) => {
    const exists = currentValues.includes(value);
    const nextValues = exists
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    updateParams({ [key]: nextValues });
  };

  const applyPreset = (nextPreset) => {
    setFilterError(null);

    if (nextPreset === "today") {
      updateParams({ preset: "today", from: undefined, to: undefined });
      return;
    }

    if (nextPreset === "yesterday") {
      const yesterdayStart = getUtcStartOfDay(-1).toISOString();
      const todayStart = getUtcStartOfDay(0).toISOString();
      updateParams({
        preset: "yesterday",
        from: yesterdayStart,
        to: todayStart,
      });
      return;
    }

    if (nextPreset === "last7") {
      const last7Start = getUtcStartOfDay(-6).toISOString();
      const now = new Date().toISOString();
      updateParams({
        preset: "last7",
        from: last7Start,
        to: now,
      });
      return;
    }

    setShowAdvancedFilters(true);
    updateParams({ preset: "custom" }, { resetPage: false });
  };

  const applyCustomDate = () => {
    setFilterError(null);
    const nextFrom = fromLocalDateTimeInputValue(customFromInput);
    const nextTo = fromLocalDateTimeInputValue(customToInput);

    if (!nextFrom || !nextTo) {
      setFilterError("Both custom From and To are required.");
      return;
    }

    if (new Date(nextFrom).getTime() > new Date(nextTo).getTime()) {
      setFilterError("From date must be before To date.");
      return;
    }

    updateParams({ preset: "custom", from: nextFrom, to: nextTo });
  };

  const applySearch = () => {
    updateParams({ search: searchDraft.trim() || undefined });
  };

  const resetFilters = () => {
    setFilterError(null);
    setSearchDraft("");
    setCustomFromInput("");
    setCustomToInput("");
    setShowAdvancedFilters(false);
    setSearchParams({});
  };

  const hasAnyNonPaginationFilter =
    status.length > 0 ||
    channels.length > 0 ||
    sourceSystems.length > 0 ||
    Boolean(search) ||
    Boolean(from) ||
    Boolean(to);

  const queryArgs = useMemo(
    () => ({
      status: status.length > 0 ? status.join(",") : undefined,
      channels: channels.length > 0 ? channels.join(",") : undefined,
      sourceSystems:
        sourceSystems.length > 0 ? sourceSystems.join(",") : undefined,
      search: search || undefined,
      from: from || undefined,
      to: to || undefined,
      page: hasAnyNonPaginationFilter || page > 1 ? page : undefined,
      pageSize: pageSize !== 50 ? pageSize : undefined,
    }),
    [
      status,
      channels,
      sourceSystems,
      search,
      from,
      to,
      page,
      pageSize,
      hasAnyNonPaginationFilter,
    ],
  );

  const { data, isLoading, isError, error, refetch } =
    useGetPosOrdersQuery(queryArgs);

  const orders = Array.isArray(data?.orders) ? data.orders : [];
  const totalCount = Number(data?.totalCount || 0);
  const serverPage = Number(data?.page || page || 1);
  const serverPageSize = Number(data?.pageSize || pageSize || 50);
  const totalPages =
    serverPageSize > 0
      ? Math.max(Math.ceil(totalCount / serverPageSize), 1)
      : 1;

  const summaryLine = `Date: ${
    preset === "today"
      ? "Today"
      : preset === "yesterday"
        ? "Yesterday"
        : preset === "last7"
          ? "Last 7 Days"
          : from && to
            ? `${new Date(from).toLocaleString()} - ${new Date(to).toLocaleString()}`
            : "Custom"
  } | Status: ${status.length > 0 ? status.join(", ") : "All"} | Channels: ${
    channels.length > 0 ? channels.join(", ") : "All"
  } | Source: ${sourceSystems.length > 0 ? sourceSystems.join(", ") : "All"}`;

  const columns = [
    { key: "orderNo", label: "Order No" },
    { key: "status", label: "Status" },
    { key: "payment", label: "Payment" },
    { key: "channel", label: "Channel" },
    { key: "source", label: "Source" },
    { key: "orderType", label: "Order Type" },
    { key: "tableSeat", label: "Table/Seat" },
    { key: "customer", label: "Name" },
    { key: "total", label: "Total" },
  ];

  const rows = orders.map((order) => ({
    id: order.orderId,
    orderNo: (
      <button
        type="button"
        onClick={() => navigate(`/pos/orders/${order.orderId}`)}
        className="font-semibold text-indigo-700 dark:text-indigo-300 hover:underline"
      >
        #{order.orderNo}
      </button>
    ),
    status: (
      <span title={`Raw status: ${order.status || "-"}`}>
        {order.displayStatus || order.status || "-"}
      </span>
    ),
    payment: order.paymentStatus || "-",
    channel: order.channel || "-",
    source: order.sourceSystem || "-",
    orderType: order.orderType || "-",
    tableSeat: order.tableNo || order.seatRef || "-",
    customer: order.customerName || "-",
    total: (
      <span className="font-semibold text-slate-900 dark:text-slate-100">
        {formatMoney(order.grandTotal)}
      </span>
    ),
  }));

  const renderFilterChip = (label, isActive, onClick) => (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1 text-xs font-semibold transition",
        isActive
          ? "border-indigo-400 bg-indigo-100 text-indigo-700 dark:border-indigo-600 dark:bg-indigo-900/30 dark:text-indigo-300"
          : "border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-4 px-6 py-6">
      <PageHeader
        title="Order List"
        subtitle="Manage POS orders across statuses, channels, and sources."
        actions={
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => navigate("/fulfillment/orders")}
            >
              Fulfillment Orders
            </Button>
            <Button size="sm" variant="secondary" onClick={resetFilters}>
              Reset Filters
            </Button>
          </div>
        }
      />

      <Card className="p-0">
        <div className="border-b border-slate-200 bg-white px-4 py-4 dark:border-slate-700 dark:bg-slate-800">
          <div className="space-y-4">
            <div className="text-xs font-semibold text-slate-600 dark:text-slate-400">
              {summaryLine}
            </div>

            <div className="flex items-end justify-end gap-2 overflow-x-auto pb-1">
              <Select
                className="h-9 min-w-[130px] px-3 pr-8 text-sm"
                value={preset}
                onChange={(e) => applyPreset(e.target.value)}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="last7">Last 7 Days</option>
                <option value="custom">Custom</option>
              </Select>

              <div className="w-full max-w-sm">
                <Input
                  className="h-9 px-3 text-sm"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder="Search order/customer/table..."
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      applySearch();
                    }
                  }}
                />
              </div>

              <Button size="sm" onClick={applySearch}>
                Apply
              </Button>

              <Button
                size="sm"
                variant="secondary"
                onClick={() => setShowAdvancedFilters((prev) => !prev)}
              >
                {showAdvancedFilters ? "Hide Filters" : "More Filters"}
              </Button>
            </div>

            {showAdvancedFilters && (
              <div className="space-y-4 border-t border-slate-200 pt-4 dark:border-slate-700">
                {preset === "custom" && (
                  <div className="grid gap-3 md:grid-cols-3">
                    <Input
                      label="From"
                      type="datetime-local"
                      value={customFromInput}
                      onChange={(e) => setCustomFromInput(e.target.value)}
                    />
                    <Input
                      label="To"
                      type="datetime-local"
                      value={customToInput}
                      onChange={(e) => setCustomToInput(e.target.value)}
                    />
                    <div className="flex items-end">
                      <Button size="sm" onClick={applyCustomDate}>
                        Apply Custom Range
                      </Button>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Status
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((value) =>
                      renderFilterChip(value, status.includes(value), () =>
                        toggleMultiFilter("status", status, value),
                      ),
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Channels
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {CHANNEL_OPTIONS.map((value) =>
                      renderFilterChip(value, channels.includes(value), () =>
                        toggleMultiFilter("channels", channels, value),
                      ),
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    Source Systems
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {SOURCE_OPTIONS.map((value) =>
                      renderFilterChip(
                        value,
                        sourceSystems.includes(value),
                        () =>
                          toggleMultiFilter(
                            "sourceSystems",
                            sourceSystems,
                            value,
                          ),
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}

            {filterError && (
              <Alert tone="error" onClose={() => setFilterError(null)}>
                {filterError}
              </Alert>
            )}
          </div>
        </div>

        <div className="space-y-4 px-4 py-4">
          {isLoading && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Loading orders...
            </p>
          )}

          {isError && (
            <Alert tone="error">
              <div className="flex items-center justify-between gap-3">
                <span>
                  {error?.data?.detail ||
                    error?.data?.message ||
                    "Failed to load orders."}
                </span>
                <Button size="sm" variant="secondary" onClick={() => refetch()}>
                  Retry
                </Button>
              </div>
            </Alert>
          )}

          {!isLoading && !isError && (
            <>
              <Table
                columns={columns}
                rows={rows}
                emptyText="No orders found."
              />

              <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-3 dark:border-slate-700">
                <div className="text-sm text-slate-600 dark:text-slate-400">
                  Total: {totalCount} | Page {serverPage} of {totalPages}
                </div>

                <div className="flex items-center gap-2">
                  <Select
                    className="min-w-[110px]"
                    value={serverPageSize}
                    onChange={(e) =>
                      updateParams({
                        pageSize: Number(e.target.value),
                      })
                    }
                  >
                    <option value={25}>25 / page</option>
                    <option value={50}>50 / page</option>
                    <option value={100}>100 / page</option>
                    <option value={200}>200 / page</option>
                  </Select>

                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={serverPage <= 1}
                    onClick={() =>
                      updateParams(
                        { page: serverPage - 1 },
                        { resetPage: false },
                      )
                    }
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={serverPage >= totalPages}
                    onClick={() =>
                      updateParams(
                        { page: serverPage + 1 },
                        { resetPage: false },
                      )
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OrderList;
